import { useEffect, useState } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import AIRewrite from './AIRewrite';

function fieldId(sectionId, key) {
  return `${sectionId}-${key}`;
}

export function Field({ sectionId, field, value, onChange, mediaType = 'image' }) {
  const { t } = useI18n();
  const id = fieldId(sectionId, field.key);
  const common = {
    id,
    name: field.key,
    value: value ?? '',
    onChange: (e) => onChange(field.key, e.target.value),
    disabled: Boolean(field.disabled),
  };

  let label = field.label;
  if (field.labelKey) {
    const translated = t(field.labelKey);
    if (translated && translated !== field.labelKey) label = translated;
  }

  let placeholder = field.placeholder || '';
  if (field.placeholderKey) {
    const translated = t(field.placeholderKey);
    if (translated && translated !== field.placeholderKey) placeholder = translated;
  }

  if (field.type === 'select') {
    return (
      <label className="Field" htmlFor={id}>
        <div className="Field-label">{label}</div>
        <select {...common}>
          {(field.options || []).map((opt) => (
            <option key={opt.value} value={opt.value} disabled={Boolean(opt.disabled)}>
              {opt.labelKey ? (t(opt.labelKey) === opt.labelKey ? opt.label : t(opt.labelKey)) : opt.label}
            </option>
          ))}
        </select>
        {field.help ? <div className="Field-help">{field.help}</div> : null}
      </label>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div className="Field">
        <label className="Field-label" htmlFor={id}>{label}</label>
        <textarea
          id={id}
          name={field.key}
          rows={field.rows || 3}
          placeholder={placeholder}
          value={value ?? ''}
          onChange={(e) => onChange(field.key, e.target.value)}
        />
        <AIRewrite value={value} onApply={(next) => onChange(field.key, next)} field={field.key === 'negativePromptExtra' ? 'negative' : 'visual'} mediaType={mediaType} disabled={field.disabled} />
        {field.help ? <div className="Field-help">{field.help}</div> : null}
      </div>
    );
  }

  if (field.type === 'number') {
    return (
      <label className="Field" htmlFor={id}>
        <div className="Field-label">{label}</div>
        <input
          id={id}
          name={field.key}
          type="number"
          min={field.min}
          max={field.max}
          step={field.step}
          value={value ?? ''}
          onChange={(e) => onChange(field.key, e.target.value)}
        />
        {field.help ? <div className="Field-help">{field.help}</div> : null}
      </label>
    );
  }

  if (field.type === 'toggle') {
    return (
      <label className="Field Field--toggle" htmlFor={id}>
        <input
          id={id}
          name={field.key}
          type="checkbox"
          checked={Boolean(value)}
          disabled={Boolean(field.disabled)}
          onChange={(e) => onChange(field.key, e.target.checked)}
        />
        <span className="Field-toggleText">
          <span>{label}</span>
          {field.help ? <span className="Field-help">{field.help}</span> : null}
        </span>
      </label>
    );
  }

  if (field.type === 'password') {
    return (
      <label className="Field" htmlFor={id}>
        <div className="Field-label">{label}</div>
        <input
          id={id}
          name={field.key}
          type="password"
          autoComplete="off"
          placeholder={placeholder}
          value={value ?? ''}
          onChange={(e) => onChange(field.key, e.target.value)}
        />
        {field.help ? <div className="Field-help">{field.help}</div> : null}
      </label>
    );
  }

  if (field.type === 'brand_select') return null;
  if (field.type === 'reference_upload') return null;

  return (
    <div className="Field">
      <label className="Field-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        name={field.key}
        type="text"
        placeholder={placeholder}
        value={value ?? ''}
        onChange={(e) => onChange(field.key, e.target.value)}
      />
      {['scene', 'beats', 'background', 'camera', 'overlayText'].includes(field.key) ? <AIRewrite value={value} onApply={(next) => onChange(field.key, next)} field={field.key === 'overlayText' ? 'caption' : 'visual'} mediaType={mediaType} disabled={field.disabled} /> : null}
      {field.help ? <div className="Field-help">{field.help}</div> : null}
    </div>
  );
}

export function AccordionCard({ title, open, onToggle, children, className }) {
  return (
    <details className={`Card Card--accordion ${className || ''}`} open={open} onToggle={onToggle}>
      <summary className="Card-title Card-title--summary">{title}</summary>
      <div className="Card-body">{children}</div>
    </details>
  );
}

function defaultOpenSections(sections, mobile) {
  const open = {};
  for (const section of sections) open[section.id] = !mobile;
  if (mobile) {
    open.output = true;
    open.network = true;
    open.creative = false;
    open.overlay = false;
    open.constraints = false;
    open.storage = false;
  }
  return open;
}

export function SettingsForm({ schema, settings, onChange, mobile }) {
  const { t } = useI18n();
  const mediaType = settings.mediaType || 'image';

  function isVisibleField(field) {
    if (Array.isArray(field.mediaTypes) && !field.mediaTypes.includes(mediaType)) return false;
    if (!field.visibleIf) return true;
    const { key, equals, notEquals } = field.visibleIf;
    if (Object.prototype.hasOwnProperty.call(field.visibleIf, 'notEquals')) return settings[key] !== notEquals;
    return settings[key] === equals;
  }

  const sections = schema
    .filter((s) => s.id !== 'brand' && s.id !== 'input')
    .filter((s) => !Array.isArray(s.mediaTypes) || s.mediaTypes.includes(mediaType))
    .map((section) => ({
      ...section,
      settingsGroup: ['network', 'constraints', 'storage', 'overlay'].includes(section.id) ? 'advanced' : 'core',
      fields: section.fields.filter(isVisibleField),
    }))
    .filter((section) => section.fields.length > 0);

  const typeCopy = {
    image: {
      title: 'Image generation settings',
      body: 'Start with size and model, then only open creative or advanced controls when you need them.',
      step: 'Create image → refine style → export',
    },
    video: {
      title: 'Video generation settings',
      body: 'Keep the first pass simple: model, duration, aspect ratio. Camera notes can come after.',
      step: 'Create video → review motion → iterate',
    },
    audio: {
      title: 'Audio settings',
      body: 'Choose a voice model, paste the script, and generate speech without image-only options in the way.',
      step: 'Write script → choose voice → generate',
    },
    text: {
      title: 'Text settings',
      body: 'Text generation stays intentionally minimal so you can focus on the copy.',
      step: 'Write brief → generate copy → refine',
    },
  };
  const currentTypeCopy = typeCopy[mediaType] || typeCopy.image;
  const [openSections, setOpenSections] = useState(() => defaultOpenSections(sections, mobile));
  const [activeSectionId, setActiveSectionId] = useState(() => {
    const first = sections[0]?.id;
    return typeof first === 'string' && first ? first : 'output';
  });

  useEffect(() => {
    setOpenSections(defaultOpenSections(sections, mobile));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobile]);

  useEffect(() => {
    // Keep active tab valid if schema changes (e.g. models loading).
    if (sections.some((s) => s.id === activeSectionId)) return;
    const first = sections[0]?.id;
    setActiveSectionId(typeof first === 'string' && first ? first : 'output');
  }, [sections, activeSectionId]);

  useEffect(() => {
    setActiveSectionId('output');
  }, [mediaType]);

  // Desktop-friendly: prefer tabs over accordions for a “filters sidebar” feel.
  if (!mobile) {
    const active = sections.find((s) => s.id === activeSectionId) || sections[0];
    return (
      <div className="Settings Settings--tabs">
        <div className="SettingsTypeHeader">
          <div>
            <div className="SettingsTypeKicker">{mediaType}</div>
            <div className="SettingsTypeTitle">{currentTypeCopy.title}</div>
            <div className="SettingsTypeBody">{currentTypeCopy.body}</div>
          </div>
        </div>
        <div className="SettingsGuide" aria-label="Suggested workflow">
          <span className="SettingsGuideDot" />
          <span>{currentTypeCopy.step}</span>
        </div>
        <div className="SettingsTabs" role="tablist" aria-label="Settings sections">
          {sections.map((section) => {
            const title =
              section.titleKey ? (t(section.titleKey) === section.titleKey ? section.title : t(section.titleKey)) : section.title;
            const isActive = section.id === activeSectionId;
            return (
              <button
                key={section.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`SettingsTab ${isActive ? 'SettingsTab--active' : ''}`}
                onClick={() => setActiveSectionId(section.id)}
              >
                <span>{title}</span>
                {section.settingsGroup === 'advanced' ? <span className="SettingsTabBadge">Advanced</span> : null}
              </button>
            );
          })}
        </div>

        {active ? (
          <div className="SettingsTabPanel" role="tabpanel">
            <div className="Grid">
              {active.fields.map((field) => (
                <Field key={field.key} sectionId={active.id} field={field} value={settings[field.key]} onChange={onChange} mediaType={settings.mediaType} />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="Settings">
      <div className="SettingsTypeHeader">
        <div>
          <div className="SettingsTypeKicker">{mediaType}</div>
          <div className="SettingsTypeTitle">{currentTypeCopy.title}</div>
          <div className="SettingsTypeBody">{currentTypeCopy.body}</div>
        </div>
      </div>
      {sections.map((section) => (
        <AccordionCard
          key={section.id}
          title={
            section.titleKey ? (t(section.titleKey) === section.titleKey ? section.title : t(section.titleKey)) : section.title
          }
          open={Boolean(openSections[section.id])}
          onToggle={(e) => {
            const nextOpen = e.target?.open;
            setOpenSections((prev) => ({ ...prev, [section.id]: Boolean(nextOpen) }));
          }}
        >
          <div className="Grid">
            {section.fields.map((field) => (
              <Field
                key={field.key}
                sectionId={section.id}
                field={field}
                value={settings[field.key]}
                onChange={onChange}
                mediaType={settings.mediaType}
              />
            ))}
          </div>
        </AccordionCard>
      ))}
    </div>
  );
}
